import React from 'react';
import ServicePageLayout from '../../components/seo/ServicePageLayout';
import { getGallery } from '../../seo/galleryConfig';

const faqs = [
  {
    q: 'How do you find a hidden water leak?',
    a: 'We start with simple checks — meter test (close all taps and read the meter for 30 minutes), visual inspection of stopcocks, joints, and accessible pipework. If the leak is hidden, we use a combination of acoustic listening equipment, thermal imaging cameras, and tracer-gas methods. Modern detection is non-destructive — we identify the leak before opening anything up.'
  },
  {
    q: 'My water bill has gone up but I cannot see a leak — could it still be a pipe?',
    a: 'Yes — very likely. A leak in your supply pipe (between the meter and your property) or under the floor will not be visible but will still register on the meter. A sustained 0.5 litre/hour leak adds ~£250 a year to most household bills. Worth investigating.'
  },
  {
    q: 'Will I need to dig up my floor?',
    a: 'Often no. Our detection equipment narrows down the location to within a few centimetres before anything is opened up. Where lifting is unavoidable (e.g. concrete floor leaks) we discuss the options before proceeding so you can decide on full vs. patch repair.'
  },
  {
    q: 'Do you provide a leak detection report for insurance?',
    a: 'Yes — every job ends with a written and photographed report identifying the leak location, the cause, the repair carried out, and any recommended follow-up. Suitable for buildings or contents insurance claims.'
  },
  {
    q: 'How long does a typical leak detection job take?',
    a: 'Most domestic leak detections are completed within 1-2 hours. Repair time depends on the location and severity. We aim to find and fix on the same visit where possible.'
  },
];

const relatedServices = [
  { slug: 'plumbing', label: 'Plumbing Services', blurb: 'Full plumbing repairs and installations.' },
  { slug: 'emergency-plumber', label: 'Emergency Plumber', blurb: 'When a leak turns into a flood — 24/7 callout.' },
  { slug: 'blocked-drains', label: 'Blocked Drains', blurb: 'Drain unblocking across Kent, London and Essex.' },
];

const LeakDetectionPage = () => (
  <ServicePageLayout
    seoTitle="Leak Detection & Water Leak Repairs in Kent, London & Essex | H.A.R.P Solutions LTD"
    seoDescription="Professional water leak detection and repairs across Kent, London and Essex. Non-destructive thermal imaging, acoustic detection, insurance-friendly reports. Call +44 330 122 1094."
    canonicalPath="/services/leak-detection-repairs"
    keywords="leak detection, water leak repair, hidden water leak, plumbing leak repairs, leak detection Kent, leak detection London, leak detection Essex"
    pageTitle="Water Leak Detection & Repairs — Non-Destructive, Insurance-Ready"
    serviceName="Leak Detection &amp; Repairs"
    serviceSlug="leak-detection-repairs"
    heroSubheading="Find the leak. Fix the cause. No unnecessary destruction."
    intro="Hidden leaks ruin floors, damage walls, push insurance premiums up, and add hundreds of pounds a year to water bills — all without you ever seeing a drip. H.A.R.P Solutions uses modern non-destructive detection (acoustic, thermal imaging, tracer gas) to pinpoint leaks accurately across Kent, London and Essex, then carries out a clean repair with full insurance-ready documentation."
    bullets={[
      'Acoustic leak detection equipment',
      'Thermal imaging for under-floor and behind-wall leaks',
      'Tracer-gas pinpointing for buried supply pipes',
      'Minimal-disruption access — no opening up until confirmed',
      'Insurance-ready reports with photos',
      'Same-visit repair where access permits',
    ]}
    faqs={faqs}
    relatedServices={relatedServices}
    galleryImages={getGallery('leak-detection-repairs')}
  >
    <h2>Signs You May Have A Hidden Leak</h2>
    <p>Most hidden leaks reveal themselves through small clues, long before you ever see standing water. Watch for:</p>
    <ul>
      <li>An unexplained increase in your water bill, especially when usage has not changed.</li>
      <li>Damp patches on ceilings, walls or skirting boards — particularly on first-floor ceilings below bathrooms.</li>
      <li>Persistent musty smells in cupboards, lofts, or under stairs.</li>
      <li>Mould or peeling paint in specific localised areas.</li>
      <li>A boiler that loses pressure repeatedly, requiring frequent re-pressurising.</li>
      <li>Warm spots on tile or concrete floors (a hot-water leak under the floor).</li>
      <li>The sound of running water when no taps are open.</li>
    </ul>

    <h2>How Modern Leak Detection Works</h2>
    <p>
      Twenty years ago, finding a hidden leak meant breaking up floors and walls hoping to get lucky. Today the process is far more
      precise. Our engineers run a multi-stage investigation:
    </p>
    <h3>1. Meter test</h3>
    <p>We start by reading your water meter, closing every internal stop, and re-reading 30 minutes later. Any movement confirms a live leak somewhere on the system.</p>
    <h3>2. Acoustic detection</h3>
    <p>Specialist ground microphones detect the characteristic high-frequency sound of pressurised water escaping through a small hole. Effective on metal and most plastic pipes.</p>
    <h3>3. Thermal imaging</h3>
    <p>An infrared camera reveals heat signatures from hot-water leaks and the cooling caused by cold-water leaks — visible right through plasterboard and screed in many cases.</p>
    <h3>4. Tracer gas</h3>
    <p>For buried supply pipes outside the building, we introduce a safe inert gas under pressure and trace its escape point at ground level with a sensitive detector.</p>
    <p>By the time we open anything up, we already know where the leak is to within centimetres.</p>

    <h2>Repairs We Carry Out</h2>
    <ul>
      <li>Pinhole repairs on copper joints and runs.</li>
      <li>Re-jointing or replacement of failed push-fit and compression fittings.</li>
      <li>Replacement of perished sections of plastic, lead or galvanised pipework.</li>
      <li>Boiler-circuit repressurising and leak-sealing where appropriate.</li>
      <li>Repair or replacement of failed radiator valves and bleed nipples.</li>
      <li>Concealed under-floor heating leak repairs.</li>
    </ul>

    <h2>Insurance, Reports &amp; What To Expect Next</h2>
    <p>
      Hidden-leak damage is often claimable on home or landlord buildings insurance. We provide every customer with an itemised report
      including thermal-image and acoustic-trace screenshots, photographs of the repair, and a clear written description of the cause and
      remedy. Most insurers accept these reports without further questions.
    </p>
    <p>
      Call <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a> to book a leak investigation, or use the form below. For a
      24/7 emergency, see our <a href="/services/emergency-plumber">emergency plumber service</a>.
    </p>
  </ServicePageLayout>
);

export default LeakDetectionPage;
